import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  Plus,
  Pencil,
  Trash2,
  Eye,
  Link2,
  Copy,
  ChevronDown,
  X,
  Upload,
  Calendar,
  Bot,
  TrendingUp,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Event, EventStats, AppUser } from '../../types';
import { eventsApi, adminApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

import { COUNTRY_CODES, splitPhone } from '../../config';

/** AI trace operation options (must match backend AI_TRACE_OPERATIONS). */
const AI_TRACE_OPERATION_OPTIONS: { value: string; label: string }[] = [
  { value: 'chat_request', label: 'chat_request' },
  { value: 'chat_request_stream', label: 'chat_request_stream' },
  { value: 'chat_llm_stream', label: 'chat_llm_stream' },
  { value: 'chat_llm', label: 'chat_llm' },
  { value: 'intent_classification', label: 'intent_classification' },
  { value: 'rag_vector_search', label: 'rag_vector_search' },
  { value: 'rag_query_embedding', label: 'rag_query_embedding' },
  { value: 'rag_chat_request', label: 'rag_chat_request' },
  { value: 'rag_chat_llm', label: 'rag_chat_llm' },
  { value: 'rag_ingest', label: 'rag_ingest' },
  { value: 'doc_processing_llm', label: 'doc_processing_llm' },
  { value: 'doc_processing_request', label: 'doc_processing_request' },
  { value: 'doc_reprocessing_request', label: 'doc_reprocessing_request' },
  { value: 'whatsapp_audio_transcription', label: 'whatsapp_audio_transcription' },
];

function buildTracingByOp(
  byOp: Record<string, boolean> | undefined,
): Record<string, boolean> | undefined {
  if (!byOp || typeof byOp !== 'object') return undefined;
  const allowed = new Set(AI_TRACE_OPERATION_OPTIONS.map((o) => o.value));
  const out: Record<string, boolean> = {};
  for (const [key, val] of Object.entries(byOp)) {
    if (allowed.has(key) && val === true) out[key] = true;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

interface FormValues {
  clientId: string;
  eventId: string;
  name: string;
  description: string;
  status: 'active' | 'inactive' | 'inprogress';
  otpChannel: 'disabled' | 'email' | 'whatsapp';
  whatsappCountryCode: string;
  whatsappPhoneNumber: string;
  whatsappFeedbackEnabled: boolean;
  whatsappFeedbackFrequency: number;
  aiTracingByOp: Record<string, boolean>;
  ragTopK: number;
  selfImprovementEnabled: boolean;
  requiredContactField: 'email' | 'phone' | 'both';
  signinPageBgColor: string;
  signinPageHeroBgColor: string;
  signinPageTitleColor: string;
  signinPageDescriptionColor: string;
  signinPageSignupText: string;
  signinPageSigninText: string;
  signinPageInstructionColor: string;
}

const defaultForm: FormValues = {
  clientId: '',
  eventId: '',
  name: '',
  description: '',
  status: 'inprogress',
  otpChannel: 'disabled',
  whatsappCountryCode: '+91',
  whatsappPhoneNumber: '',
  whatsappFeedbackEnabled: false,
  whatsappFeedbackFrequency: 3,
  aiTracingByOp: Object.fromEntries(AI_TRACE_OPERATION_OPTIONS.map((o) => [o.value, false])),
  ragTopK: 2,
  selfImprovementEnabled: false,
  requiredContactField: 'email',
  signinPageBgColor: '',
  signinPageHeroBgColor: '',
  signinPageTitleColor: '',
  signinPageDescriptionColor: '',
  signinPageSignupText: '',
  signinPageSigninText: '',
  signinPageInstructionColor: '',
};

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-green-100 text-green-700 border-green-200' },
  inactive: { label: 'Inactive', className: 'bg-gray-100 text-gray-600 border-gray-200' },
  inprogress: { label: 'In Progress', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
};

const PAGE_SIZE = 10;

const Events: React.FC = () => {
  const { isAdmin, refreshUser } = useAuth();

  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [form, setForm] = useState<FormValues>(defaultForm);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Stats modal
  const [statsOpen, setStatsOpen] = useState(false);
  const [selectedStats, setSelectedStats] = useState<EventStats | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedEventDetails, setSelectedEventDetails] = useState<Event | null>(null);

  // Self-improvement disable confirmation
  const [selfImprovementWarnPending, setSelfImprovementWarnPending] = useState(false);

  // WhatsApp phone conflict modal
  const [whatsappConflict, setWhatsappConflict] = useState<{
    eventName: string;
    eventId: string;
  } | null>(null);

  // Clients (admin only)
  const [clients, setClients] = useState<AppUser[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);

  // Filters
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState(isAdmin ? 'all' : 'active');
  const [clientFilter, setClientFilter] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchEvents(); }, [statusFilter, clientFilter, sortBy, sortOrder]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(fetchEvents, 300);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (isAdmin && clients.length === 0) fetchClients(); }, [isAdmin]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setPage(1);
      const params: any = { limit: 100, sortBy, sortOrder };
      if (searchText) params.search = searchText;
      if (isAdmin && statusFilter !== 'all') {
        if (statusFilter === 'inprogress') {
          params.status = 'inprogress';
        } else {
          params.isActive = statusFilter === 'active';
          if (statusFilter === 'inactive') params.status = 'inactive';
        }
      }
      if (isAdmin && clientFilter) params.clientId = clientFilter;
      const response = await eventsApi.getAll(params);
      setEvents(response.data.data || []);
    } catch {
      toast.error('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      setLoadingClients(true);
      const response = await adminApi.getAllUsers({ role: 'client', limit: 1000, isActive: true });
      if (response.data.success && response.data.data) {
        setClients(response.data.data.users || []);
      }
    } catch {
      toast.error('Failed to load clients');
    } finally {
      setLoadingClients(false);
    }
  };

  const openCreate = () => {
    if (!isAdmin) { toast.error('Only admins can create events'); return; }
    setEditingEvent(null);
    setForm(defaultForm);
    setImageFile(null);
    setPreviewUrl('');
    setAdvancedOpen(false);
    setModalOpen(true);
    if (clients.length === 0 && !loadingClients) fetchClients();
  };

  const openEdit = (event: Event) => {
    const clientIdValue =
      typeof event.clientId === 'object' && event.clientId !== null
        ? (event.clientId as { _id: string })._id
        : (event.clientId as string) || '';
    const byOp =
      event.aiTracingEnabledByOperation &&
      Object.keys(event.aiTracingEnabledByOperation).length > 0
        ? event.aiTracingEnabledByOperation
        : event.aiTracingEnabled
        ? Object.fromEntries(AI_TRACE_OPERATION_OPTIONS.map((o) => [o.value, true]))
        : {};
    const resolvedOtpChannel: FormValues['otpChannel'] =
      (event as any).otpChannel ||
      (event.requiresEmailVerification ? 'email' : 'disabled');

    setForm({
      clientId: clientIdValue,
      eventId: event.eventId,
      name: event.name,
      description: event.description,
      status: (event.status || (event.isActive ? 'active' : 'inactive')) as FormValues['status'],
      otpChannel: resolvedOtpChannel,
      aiTracingByOp: Object.fromEntries(
        AI_TRACE_OPERATION_OPTIONS.map((o) => [o.value, byOp[o.value] === true]),
      ),
      ragTopK: event.ragTopK ?? 2,
      ...(() => {
        const storedCc = (event as any).whatsappCountryCode as string | undefined;
        if (storedCc) {
          return {
            whatsappCountryCode: storedCc.startsWith('+') ? storedCc : `+${storedCc}`,
            whatsappPhoneNumber: (event as any).whatsappPhoneNumber ?? '',
          };
        }
        // Fallback: old events stored a concatenated full number — split it.
        const { countryCode, localNumber } = splitPhone((event as any).whatsappPhoneNumber ?? '');
        return { whatsappCountryCode: countryCode, whatsappPhoneNumber: localNumber };
      })(),
      whatsappFeedbackEnabled: (event as any).whatsappFeedback?.enabled ?? false,
      whatsappFeedbackFrequency: (event as any).whatsappFeedback?.frequency ?? 3,
      selfImprovementEnabled: event.selfImprovementEnabled ?? false,
      requiredContactField: (() => {
        const re = (event as any).requireEmail ?? false;
        const rp = (event as any).requirePhone ?? false;
        return re && rp ? 'both' : rp ? 'phone' : 'email';
      })() as 'email' | 'phone' | 'both',
      signinPageBgColor: (event as any).signinPageBgColor ?? '',
      signinPageHeroBgColor: (event as any).signinPageHeroBgColor ?? '',
      signinPageTitleColor: (event as any).signinPageTitleColor ?? '',
      signinPageDescriptionColor: (event as any).signinPageDescriptionColor ?? '',
      signinPageSignupText: (event as any).signinPageSignupText ?? '',
      signinPageSigninText: (event as any).signinPageSigninText ?? '',
      signinPageInstructionColor: (event as any).signinPageInstructionColor ?? '',
    });
    setEditingEvent(event);
    setImageFile(null);
    setPreviewUrl((event.imageUrl as string) || '');
    setAdvancedOpen(false);
    setModalOpen(true);
    if (isAdmin && clients.length === 0 && !loadingClients) fetchClients();
  };

  const handleDelete = async (eventId: string) => {
    try {
      await eventsApi.delete(eventId);
      toast.success('Event deleted successfully');
      fetchEvents();
    } catch {
      toast.error('Failed to delete event');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const tracingByOp = buildTracingByOp(form.aiTracingByOp);
    const anyTracingEnabled = !!(tracingByOp && Object.keys(tracingByOp).length > 0);
    try {
      setSaving(true);
      let savedId: string | undefined;
      const base: any = {
        name: form.name,
        description: form.description,
        otpChannel: form.otpChannel,
        requiresEmailVerification: form.otpChannel === 'email',
        aiTracingEnabled: anyTracingEnabled,
        aiTracingEnabledByOperation: tracingByOp,
        ragTopK: form.ragTopK,
        selfImprovementEnabled: form.selfImprovementEnabled,
        requireEmail: form.requiredContactField === 'email' || form.requiredContactField === 'both',
        requirePhone: form.requiredContactField === 'phone' || form.requiredContactField === 'both',
        whatsappCountryCode: form.whatsappPhoneNumber.trim()
          ? form.whatsappCountryCode.replace('+', '')
          : null,
        whatsappPhoneNumber: form.whatsappPhoneNumber.trim() || null,
        whatsappFeedback: {
          enabled: form.whatsappFeedbackEnabled,
          frequency: form.whatsappFeedbackFrequency,
        },
        signinPageBgColor: form.signinPageBgColor || null,
        signinPageHeroBgColor: form.signinPageHeroBgColor || null,
        signinPageTitleColor: form.signinPageTitleColor || null,
        signinPageDescriptionColor: form.signinPageDescriptionColor || null,
        signinPageSignupText: form.signinPageSignupText || null,
        signinPageSigninText: form.signinPageSigninText || null,
        signinPageInstructionColor: form.signinPageInstructionColor || null,
      };

      console.log('[handleSubmit] requiredContactField:', form.requiredContactField, '→ requireEmail:', base.requireEmail, 'requirePhone:', base.requirePhone);

      if (editingEvent) {
        const submitData = {
          ...base,
          status: form.status,
          isActive: form.status === 'active',
          ...(isAdmin && form.clientId ? { clientId: form.clientId } : {}),
        };
        const resp = await eventsApi.update(editingEvent._id, submitData);
        savedId = resp.data.data?._id || editingEvent._id;
        toast.success('Event updated successfully');
      } else {
        if (!form.clientId) { toast.error('Please select a client'); setSaving(false); return; }
        const submitData = {
          ...base,
          eventId: form.eventId,
          clientId: form.clientId,
          status: 'inprogress',
          isActive: false,
        };
        const resp = await eventsApi.create(submitData);
        savedId = resp.data.data?._id;
        toast.success('Event created successfully');
      }

      if (savedId && imageFile) {
        try {
          await eventsApi.uploadImage(savedId, imageFile);
          toast.success('Hero image uploaded');
        } catch {
          toast.warning('Event saved, but image upload failed');
        }
      }
      setModalOpen(false);
      fetchEvents();
      // Refresh user profile so selfImprovementEnabled in sidebar stays in sync
      refreshUser();
    } catch (error: any) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      if (status === 409 && data?.data?.conflict) {
        setWhatsappConflict(data.data.conflict);
        return;
      }
      toast.error(
        data?.message ||
          (editingEvent ? 'Failed to update event' : 'Failed to create event'),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleViewStats = async (event: Event) => {
    try {
      const response = await eventsApi.getStats(event._id);
      setSelectedStats(response.data.data || null);
      setStatsOpen(true);
    } catch {
      toast.error('Failed to fetch event statistics');
    }
  };

  const handleViewDetails = (event: Event) => {
    setSelectedEventDetails(event);
    setDetailsOpen(true);
  };

  const getStatusCfg = (event: Event) => {
    const s = event.status || (event.isActive ? 'active' : 'inactive');
    return STATUS_CONFIG[s] || STATUS_CONFIG.inactive;
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const totalPages = Math.ceil(events.length / PAGE_SIZE);
  const pagedEvents = events.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const filterCols = isAdmin ? 'md:grid-cols-5' : 'md:grid-cols-3';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <Bot className="w-6 h-6 text-blue-600" />
            Knowledge Assistants Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Create and manage your AI knowledge assistants
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Assistant
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className={`grid grid-cols-1 gap-4 ${filterCols}`}>
          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Client</label>
              <select
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Clients</option>
                {clients.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or description"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inprogress">In Progress</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="createdAt">Date Created</option>
              <option value="updatedAt">Last Updated</option>
              <option value="name">Name</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Order</label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {[
                    'Assistant ID',
                    'Name',
                    'Description',
                    'Status',
                    ...(isAdmin ? ['Client'] : []),
                    'Landing Page',
                    'Created',
                    'Actions',
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pagedEvents.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin ? 8 : 7} className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <Calendar className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-gray-500 font-medium">No assistants found</p>
                        <p className="text-sm text-gray-400 mt-1">
                          {isAdmin
                            ? 'Create your first assistant to get started.'
                            : 'No active knowledge assistants available.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  pagedEvents.map((event) => {
                    const scfg = getStatusCfg(event);
                    const landingUrl = `${window.location.origin}/event/${event._id}`;
                    const client =
                      typeof event.clientId === 'object' ? event.clientId : null;
                    return (
                      <tr key={event._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-700">
                          {event.eventId}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {event.name}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                          {event.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${scfg.className}`}
                          >
                            {scfg.label}
                          </span>
                        </td>
                        {isAdmin && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {client ? (
                              <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                                {client.name}
                              </span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => window.open(landingUrl, '_blank')}
                              className="p-1.5 hover:bg-gray-100 rounded transition-colors text-gray-500"
                              title="Open landing page"
                            >
                              <Link2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(landingUrl);
                                toast.success('Landing page URL copied');
                              }}
                              className="p-1.5 hover:bg-gray-100 rounded transition-colors text-gray-500"
                              title="Copy URL"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(event.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleViewDetails(event)}
                              className="p-1.5 hover:bg-gray-100 rounded transition-colors text-gray-500"
                              title="View details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleViewStats(event)}
                              className="p-1.5 hover:bg-gray-100 rounded transition-colors text-gray-500"
                              title="View statistics"
                            >
                              <TrendingUp className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openEdit(event)}
                              className="p-1.5 hover:bg-gray-100 rounded transition-colors text-gray-500"
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            {isAdmin && (
                              <button
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      'Are you sure you want to delete this event?',
                                    )
                                  )
                                    handleDelete(event._id);
                                }}
                                className="p-1.5 hover:bg-red-50 rounded transition-colors text-red-500"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {events.length > PAGE_SIZE && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <span className="text-sm text-gray-500">Total {events.length} assistants</span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Prev
              </button>
              <span className="px-3 py-1.5 text-sm text-gray-600">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setModalOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingEvent ? 'Edit Knowledge Assistant' : 'Create Knowledge Assistant'}
              </h2>
              <button
                onClick={() => setModalOpen(false)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Client (admin only) */}
              {isAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client {!editingEvent && <span className="text-red-500">*</span>}
                  </label>
                  <select
                    value={form.clientId}
                    onChange={(e) => setForm((v) => ({ ...v, clientId: e.target.value }))}
                    required={!editingEvent}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">
                      {loadingClients ? 'Loading...' : 'Select a client'}
                    </option>
                    {clients.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.name} ({c.email})
                      </option>
                    ))}
                  </select>
                  {editingEvent && (
                    <p className="text-xs text-gray-500 mt-1">
                      Changing the client reassigns this event to another client.
                    </p>
                  )}
                </div>
              )}

              {/* Event ID (create only) */}
              {!editingEvent && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Knowledge Assistant ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.eventId}
                    onChange={(e) => setForm((v) => ({ ...v, eventId: e.target.value }))}
                    required
                    placeholder="e.g., ai-course-2024"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              {/* Status (edit only, admin only) */}
              {editingEvent && isAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) =>
                      setForm((v) => ({ ...v, status: e.target.value as FormValues['status'] }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inprogress">In Progress</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              )}

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="text-red-500">*</span> Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((v) => ({ ...v, name: e.target.value }))}
                  required
                  placeholder="e.g., Artificial Intelligence Course 2024"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="text-red-500">*</span> Description
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((v) => ({ ...v, description: e.target.value }))}
                  required
                  rows={3}
                  placeholder="Brief description of the event and its content"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Registration requirements */}
              <div className="p-4 border border-gray-200 rounded-lg space-y-2">
                <div>
                  <p className="text-sm font-medium text-gray-700">Required Contact Field</p>
                  <p className="text-xs text-gray-500 mt-0.5">Which contact field is mandatory on the signup form.</p>
                </div>
                <select
                  value={form.requiredContactField}
                  onChange={(e) =>
                    setForm((v) => ({ ...v, requiredContactField: e.target.value as 'email' | 'phone' | 'both' }))
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="email">Email only</option>
                  <option value="phone">Phone only</option>
                  <option value="both">Both email and phone</option>
                </select>
              </div>

              {/* OTP verification channel */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  OTP Verification Channel
                </label>
                <select
                  value={form.otpChannel}
                  onChange={(e) =>
                    setForm((v) => ({ ...v, otpChannel: e.target.value as FormValues['otpChannel'] }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="disabled">Disabled</option>
                  <option value="email">Email OTP</option>
                  <option value="whatsapp">WhatsApp OTP</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {form.otpChannel === 'email' && 'Attendees must verify their email via OTP before starting chat.'}
                  {form.otpChannel === 'whatsapp' && 'Attendees must verify their phone number via WhatsApp OTP before starting chat.'}
                  {form.otpChannel === 'disabled' && 'No OTP verification required.'}
                </p>
              </div>

              {/* WhatsApp Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  WhatsApp Number <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <div className="flex gap-2">
                  <select
                    value={form.whatsappCountryCode}
                    onChange={(e) => setForm((v) => ({ ...v, whatsappCountryCode: e.target.value }))}
                    className="rounded-lg border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.code} value={c.code}>{c.code} {c.name}</option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={form.whatsappPhoneNumber}
                    onChange={(e) => setForm((v) => ({ ...v, whatsappPhoneNumber: e.target.value.replace(/\D/g, '') }))}
                    placeholder="e.g. 9187227364"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Link a WhatsApp Business number to this assistant. Inbound messages to this number will be routed here.
                </p>
              </div>

              {/* WhatsApp Feedback */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-700">WhatsApp Feedback</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Prompt users for feedback (👍/👎) periodically during WhatsApp chats.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setForm((v) => ({ ...v, whatsappFeedbackEnabled: !v.whatsappFeedbackEnabled }))}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      form.whatsappFeedbackEnabled ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        form.whatsappFeedbackEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
                {form.whatsappFeedbackEnabled && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Ask for feedback every N responses
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={form.whatsappFeedbackFrequency}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/\D/g, '');
                        const num = parseInt(raw, 10);
                        if (raw === '') {
                          setForm((v) => ({ ...v, whatsappFeedbackFrequency: 1 }));
                        } else if (!isNaN(num)) {
                          setForm((v) => ({ ...v, whatsappFeedbackFrequency: Math.min(20, Math.max(1, num)) }));
                        }
                      }}
                      className="w-24 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                )}
              </div>

              {/* Hero Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hero Image</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
                  <input
                    type="file"
                    id="hero-image-input"
                    accept=".png,.jpg,.jpeg,.gif,.webp,.svg"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="hero-image-input"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-3">
                      <Upload className="w-6 h-6 text-blue-600" />
                    </div>
                    <p className="text-sm font-medium text-gray-700">
                      Click or drag image to upload
                    </p>
                    <p className="text-xs text-gray-500 mt-1">PNG, JPG, JPEG, GIF, WEBP, or SVG</p>
                  </label>
                </div>
                {previewUrl && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">Preview</p>
                    <div className="flex justify-center bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <img
                        src={previewUrl}
                        alt="Hero preview"
                        className="max-h-48 rounded object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Signup/Signin page */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Signup/Signin Page</p>
                    <p className="text-xs text-gray-500 mt-0.5">Customise the appearance of the public signup/signin page. Leave blank to use defaults.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setForm((v) => ({
                      ...v,
                      signinPageBgColor: '',
                      signinPageHeroBgColor: '',
                      signinPageTitleColor: '',
                      signinPageDescriptionColor: '',
                      signinPageSignupText: '',
                      signinPageSigninText: '',
                      signinPageInstructionColor: '',
                    }))}
                    className="shrink-0 text-xs text-gray-500 hover:text-gray-700 underline underline-offset-2"
                  >
                    Reset to defaults
                  </button>
                </div>
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Page Background Colour</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={form.signinPageBgColor || '#ffffff'}
                          onChange={(e) => setForm((v) => ({ ...v, signinPageBgColor: e.target.value }))}
                          className="h-8 w-10 rounded border border-gray-300 cursor-pointer p-0.5"
                        />
                        <input
                          type="text"
                          value={form.signinPageBgColor}
                          onChange={(e) => setForm((v) => ({ ...v, signinPageBgColor: e.target.value }))}
                          placeholder="#ffffff"
                          className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Hero Container Background</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={form.signinPageHeroBgColor && !form.signinPageHeroBgColor.includes('gradient') ? form.signinPageHeroBgColor : '#667eea'}
                          onChange={(e) => setForm((v) => ({ ...v, signinPageHeroBgColor: e.target.value }))}
                          className="h-8 w-10 rounded border border-gray-300 cursor-pointer p-0.5"
                        />
                        <input
                          type="text"
                          value={form.signinPageHeroBgColor}
                          onChange={(e) => setForm((v) => ({ ...v, signinPageHeroBgColor: e.target.value }))}
                          placeholder="e.g. #667eea or linear-gradient(...)"
                          className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Title Font Colour</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={form.signinPageTitleColor || '#ffffff'}
                          onChange={(e) => setForm((v) => ({ ...v, signinPageTitleColor: e.target.value }))}
                          className="h-8 w-10 rounded border border-gray-300 cursor-pointer p-0.5"
                        />
                        <input
                          type="text"
                          value={form.signinPageTitleColor}
                          onChange={(e) => setForm((v) => ({ ...v, signinPageTitleColor: e.target.value }))}
                          placeholder="#ffffff"
                          className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Description Font Colour</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={form.signinPageDescriptionColor || '#ffffff'}
                          onChange={(e) => setForm((v) => ({ ...v, signinPageDescriptionColor: e.target.value }))}
                          className="h-8 w-10 rounded border border-gray-300 cursor-pointer p-0.5"
                        />
                        <input
                          type="text"
                          value={form.signinPageDescriptionColor}
                          onChange={(e) => setForm((v) => ({ ...v, signinPageDescriptionColor: e.target.value }))}
                          placeholder="#ffffff"
                          className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Sign Up Instruction Text</label>
                    <input
                      type="text"
                      value={form.signinPageSignupText}
                      onChange={(e) => setForm((v) => ({ ...v, signinPageSignupText: e.target.value }))}
                      placeholder="Create your account to start interacting with our Knowledge Companion"
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Sign In Instruction Text</label>
                    <input
                      type="text"
                      value={form.signinPageSigninText}
                      onChange={(e) => setForm((v) => ({ ...v, signinPageSigninText: e.target.value }))}
                      placeholder="Sign in to continue interacting with our Knowledge Companion"
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Instruction Text Colour</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={form.signinPageInstructionColor || '#6b7280'}
                        onChange={(e) => setForm((v) => ({ ...v, signinPageInstructionColor: e.target.value }))}
                        className="h-8 w-10 rounded border border-gray-300 cursor-pointer p-0.5"
                      />
                      <input
                        type="text"
                        value={form.signinPageInstructionColor}
                        onChange={(e) => setForm((v) => ({ ...v, signinPageInstructionColor: e.target.value }))}
                        placeholder="#6b7280"
                        className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Self-Improvement */}
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-700">Self-Improvement</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Review unanswered questions and knowledge gaps for this assistant. Requires AI Tracing.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (form.selfImprovementEnabled) {
                      setSelfImprovementWarnPending(true);
                    } else {
                      setForm((v) => ({
                        ...v,
                        selfImprovementEnabled: true,
                        aiTracingByOp: { ...v.aiTracingByOp, chat_llm_stream: true, chat_llm: true },
                      }));
                    }
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                    form.selfImprovementEnabled ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      form.selfImprovementEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Advanced (admin only) */}
              {isAdmin && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setAdvancedOpen((v) => !v)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    Advanced Settings
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${advancedOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {advancedOpen && (
                    <div className="p-4 space-y-5">
                      {/* AI Tracing */}
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">AI Tracing</p>
                        <p className="text-xs text-gray-500 mb-3">
                          Choose which operations to trace. Traces appear in admin AI Traces.
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          {AI_TRACE_OPERATION_OPTIONS.map(({ value, label }) => {
                            const lockedBySelfImprovement =
                              form.selfImprovementEnabled &&
                              (value === 'chat_llm_stream' || value === 'chat_llm');
                            return (
                              <label
                                key={value}
                                className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={form.aiTracingByOp[value] ?? false}
                                  onChange={(e) => {
                                    if (lockedBySelfImprovement && !e.target.checked) {
                                      toast.error('Cannot deselect — this operation is required while Self-Improvement is enabled.');
                                      return;
                                    }
                                    setForm((v) => ({
                                      ...v,
                                      aiTracingByOp: { ...v.aiTracingByOp, [value]: e.target.checked },
                                    }));
                                  }}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                {label}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      {/* RAG top-K */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          RAG chunks (top K)
                        </label>
                        <p className="text-xs text-gray-500 mb-2">
                          Number of document chunks to retrieve per chat message. Default 2.
                        </p>
                        <input
                          type="number"
                          min={1}
                          max={20}
                          value={form.ragTopK}
                          onChange={(e) =>
                            setForm((v) => ({ ...v, ragTopK: Number(e.target.value) }))
                          }
                          className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Saving...' : editingEvent ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stats Modal */}
      {statsOpen && selectedStats && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setStatsOpen(false)}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Knowledge Assistant Statistics</h2>
              <button
                onClick={() => setStatsOpen(false)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              {[
                { label: 'Total Documents', value: selectedStats.totalDocuments },
                { label: 'Total Guardrails', value: selectedStats.totalGuardrails },
                { label: 'Behavioral Prompts', value: selectedStats.totalBehavioralPrompts },
                { label: 'Total Sessions', value: selectedStats.totalSessions },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                >
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{label}</p>
                  <p className="text-2xl font-semibold text-gray-900">{value ?? 0}</p>
                </div>
              ))}
              <div className="col-span-2 bg-green-50 rounded-lg p-4 border border-green-200">
                <p className="text-xs text-green-600 uppercase tracking-wider mb-1">
                  Active Today
                </p>
                <p className="text-2xl font-semibold text-green-700">
                  {selectedStats.activeToday ?? 0}
                </p>
              </div>
            </div>
            <div className="px-6 pb-6 flex justify-end">
              <button
                onClick={() => setStatsOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {detailsOpen && selectedEventDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setDetailsOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Assistant Details</h2>
              <button
                onClick={() => setDetailsOpen(false)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Assistant ID</p>
                <p className="text-gray-900 font-mono">{selectedEventDetails.eventId}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Name</p>
                <p className="text-gray-900">{selectedEventDetails.name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Description</p>
                <p className="text-gray-700 whitespace-pre-wrap">
                  {selectedEventDetails.description || '—'}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Status</p>
                  <p className="text-gray-900">{getStatusCfg(selectedEventDetails).label}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Created</p>
                  <p className="text-gray-900">
                    {new Date(selectedEventDetails.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 pb-6 flex justify-end">
              <button
                onClick={() => setDetailsOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Self-Improvement Disable Confirmation */}
      {selfImprovementWarnPending && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSelfImprovementWarnPending(false)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-gray-900 mb-1">Disable Self-Improvement?</h3>
                <p className="text-sm text-gray-600">
                  Turning this off will deselect the AI trace operations required for Self-Improvement
                  (<span className="font-medium">chat_llm_stream</span> and <span className="font-medium">chat_llm</span>)
                  and remove the Self-Improvement section from the sidebar.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setSelfImprovementWarnPending(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelfImprovementWarnPending(false);
                  setForm((v) => ({
                    ...v,
                    selfImprovementEnabled: false,
                    aiTracingByOp: { ...v.aiTracingByOp, chat_llm_stream: false, chat_llm: false },
                  }));
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Yes, Disable
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Phone Conflict Modal */}
      {whatsappConflict && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setWhatsappConflict(null)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-gray-900 mb-1">WhatsApp Number Already in Use</h3>
                <p className="text-sm text-gray-600 mb-4">
                  This WhatsApp number is already assigned to another Knowledge Assistant. The change cannot be saved.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                  <p className="text-xs font-medium text-amber-800 uppercase tracking-wide mb-2">Currently assigned to</p>
                  <p className="text-sm text-gray-800">
                    <span className="font-medium">Name:</span> {whatsappConflict.eventName}
                  </p>
                  <p className="text-sm text-gray-800">
                    <span className="font-medium">ID:</span> {whatsappConflict.eventId}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setWhatsappConflict(null)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                OK, Got It
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;
